import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n } from '@lingui/react'
import env from 'env'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'
import filter from 'lodash/fp/filter'
import flatMap from 'lodash/fp/flatMap'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'
import urlFor from 'shared/tools/url-helper'
import bookHelper from 'shared/tools/book-helper'
import audioBookHelper from 'shared/tools/audiobook-helper'
import comicbookHelper from 'shared/tools/comicbook-helper'
import deviceHelper from 'shared/tools/device-helper'

import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserSubHeaderBooksBox from 'client/bookmate/boxes/user-sub-header-books-box'
import HeaderBox from 'client/shared/boxes/header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'
import { UserLayoutSwitcherBox } from 'client/bookmate/boxes/user-layout-switcher-box'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { Heading } from 'client/shared/blocks/heading'
import { Book } from 'client/shared/blocks/book'
import AudioBook from 'client/bookmate/blocks/audiobook'
import { Comicbook } from 'client/shared/blocks/comicbook'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'

import Spacer from 'client/shared/blocks/spacer'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import LayoutUserSection from 'client/shared/blocks/layout-user-section'
import { UserSectionNav } from 'client/bookmate/blocks/user-section-nav'
import EmptyState from 'client/bookmate/blocks/empty-state'
import { Tab } from 'client/shared/blocks/tab'
import List from 'client/bookmate/blocks/list'
import { Loader } from 'client/shared/blocks/loader'
import {
  UserSectionNavigationWrapper,
  UserSectionContentsWrapper,
} from 'client/bookmate/blocks/user-books-wrapper'
import { privacyApiConstants } from '../blocks/user-settings-privacy/user-settings-privacy'

import {
  buildUser,
  loadBooks,
  loadAudioBooks,
  loadComicbooks,
  USER_BOOKS_ALL,
  USER_BOOKS_RECENT,
  USER_BOOKS_FINISHED,
  USER_BOOKS_NOT_FINISHED,
  USER_BOOKS_UPLOADS,
} from 'client/bookmate/reducers/user-reducer'

import { getBooksByIds } from 'client/bookmate/selectors/book-selector'

import { getAudioBooksByIds } from 'client/bookmate/reducers/audiobooks-reducer'

import { getComicbooksByIds } from 'client/bookmate/reducers/comicbooks-reducer'

import SVGInline from 'react-svg-inline'
import BookmarkIcon from 'client/shared/icons/bookmark.svg'
import PlusIcon from 'client/shared/icons/plus.svg'
import FlagIcon from 'client/shared/icons/flag.svg'
import UploadIcon from 'client/shared/icons/upload.svg'
import BookIcon from 'client/shared/icons/book.svg'
import LockIcon from 'client/shared/icons/lock.svg'

const warningBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  boxSizing: 'border-box',
  borderRadius: '8px',
  gap: '16px',
  background: '#22201F0D',
  color: '#9a938d',
  fontSize: '13px',
  lineHeight: '18px',
}

export type userBooksProps = {
  params: { username: string; filter: string }
  route: { content: string }
}

export const shouldUpdateParams = (
  prevProps: userBooksProps,
  currentProps: userBooksProps,
): boolean => {
  if (prevProps.params.username !== currentProps.params.username) {
    return true
  } else if (prevProps.params.filter !== currentProps.params.filter) {
    return true
  } else {
    const previousContentType = prevProps.route.content
    const currentContentType = currentProps.route.content
    return previousContentType !== currentContentType
  }
}

const infoByFilter = {
  all: {
    locale: 'all',
    icon: BookIcon,
  },
  recent: {
    locale: 'started',
    icon: BookmarkIcon,
  },
  not_finished: {
    locale: 'added',
    icon: PlusIcon,
  },
  finished: {
    locale: 'finished',
    icon: FlagIcon,
  },
  uploads: {
    locale: 'own_uploads',
    icon: UploadIcon,
  },
}

const getBooksReadingList = compose(
  join(', '),
  flatMap(book => [book.title, ...map('name', book.authors)]),
)

class UserBooksBox extends Component {
  load = () => {
    const {
      dispatch,
      currentUser: {
        data: { comics: areComicsAvailable },
      },
      user: { login, pages },
      params: { filter: _filter },
      route: { content },
    } = this.props

    if (content === 'books') {
      const booksPage = pages.books[_filter]
      return dispatch(loadBooks(login, _filter, booksPage, 20, true))
    } else if (content === 'audiobooks') {
      const audiobooksPage = pages.audiobooks[_filter]
      return dispatch(loadAudioBooks(login, _filter, audiobooksPage, 20, true))
    } else if (content === 'comicbooks' && areComicsAvailable) {
      const comicbooksPage = pages.comicbooks[_filter]
      return dispatch(loadComicbooks(login, _filter, comicbooksPage, 20, true))
    }
  }

  get booksKind() {
    const {
      user: {
        ui: { layout },
      },
    } = this.props

    if (layout === 'list') {
      return 'list'
    } else {
      return 'grid'
    }
  }

  render() {
    const {
      app,
      user,
      user: { myself },
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        {myself ? <UserSubHeaderBooksBox /> : <UserInfoBox user={user} />}
        <UserSubHeaderBox hidden={myself} />
        <Spacer size={32} />
        {deviceHelper.isDesktopAllSizes(app)
          ? this.renderDesktop()
          : this.renderMobile()}
      </>
    )
  }

  renderMeta() {
    const {
      user: { name = '', login: username = '', counters = {} },
      i18n,
      books,
    } = this.props
    const readingBooks = filter({ library_card: { state: 'reading' } }, books)

    const title = `${name || i18n._('profile.meta_title')} (@${username})`

    let dscrSuffix = ''
    if (readingBooks.length) {
      dscrSuffix = `, ${i18n._('profile.meta_description_books_remainder', {
        books: getBooksReadingList(readingBooks),
      })}`
    }

    const description =
      i18n._('profile.meta_description_books', {
        count: counters.total,
        username,
        name,
      }) + dscrSuffix

    return (
      <>
        <MetaTitle title={title} />
        <MetaDescription description={description} />
      </>
    )
  }

  renderDesktop() {
    const {
      params: { filter: _filter },
      currentUser: {
        ui: {
          privacy: {
            data: { content_visibility },
          },
        },
      },
      user,
      currentUser,
    } = this.props

    const shouldShowWarning =
      content_visibility === privacyApiConstants.categoryHidden &&
      user.id === currentUser.data.id

    return (
      <LayoutUserSection
        firstColumn={
          <>
            {shouldShowWarning && <Spacer size={80} />}
            {this.renderSectionNavigation()}
          </>
        }
        secondColumn={
          <div>
            {shouldShowWarning && (
              <>
                <div style={warningBoxStyles}>
                  <SVGInline svg={LockIcon} width="24px" height="24px" />
                  <span>
                    <Trans id="profile.settings_privacy_showcase_warning_books" />
                  </span>
                </div>
                <Spacer size={24} />
              </>
            )}
            <UserSectionNavigationWrapper>
              <Heading looksLike={2}>
                <Trans id={`profile.${infoByFilter[_filter].locale}`} />
              </Heading>
              {this.renderLayoutSwitcher()}
            </UserSectionNavigationWrapper>
            <Spacer size={32} />
            {this.renderSectionContents()}
          </div>
        }
      />
    )
  }

  renderMobile() {
    const {
      app,
      currentUser: {
        ui: {
          privacy: {
            data: { content_visibility },
          },
        },
      },
      user,
      currentUser,
    } = this.props

    const shouldShowWarning =
      content_visibility === privacyApiConstants.categoryHidden &&
      user.id === currentUser.data.id

    return (
      <LayoutColumn>
        {shouldShowWarning && (
          <>
            <div style={warningBoxStyles}>
              <SVGInline svg={LockIcon} width="24px" height="24px" />
              <span>
                <Trans id="profile.settings_privacy_showcase_warning_books" />
              </span>
            </div>
            <Spacer size={24} />
          </>
        )}
        <UserSectionNavigationWrapper>
          {this.renderSectionNavigation()}
          {this.renderLayoutSwitcher()}
        </UserSectionNavigationWrapper>
        <Spacer size={deviceHelper.isTabletSize(app) ? 32 : 40} />
        {this.renderSectionContents()}
      </LayoutColumn>
    )
  }

  renderSectionNavigation() {
    const {
      route: { content },
      app,
    } = this.props
    const tabKind = deviceHelper.isTabletSize(app) ? 'compact' : 'column'

    if (content === 'books') {
      return this.renderSectionNavigationForBooks(tabKind)
    } else if (content === 'audiobooks') {
      return this.renderSectionNavigationForAudiobooks(tabKind)
    } else if (content === 'comicbooks') {
      return this.renderSectionNavigationForComicbooks(tabKind)
    }
  }

  renderTab(url, count, kind, icon, message) {
    const { app } = this.props

    return (
      <Tab path={url} count={count} kind={kind}>
        {!deviceHelper.isTabletSize(app) && <SVGInline svg={icon} />}
        <Trans id={message} />
      </Tab>
    )
  }

  renderSectionNavigationForBooks(tabKind) {
    const {
      user: {
        login,
        myself,
        counters: { total, reading, finished, read_later, uploaded },
      },
      app,
    } = this.props

    return (
      <UserSectionNav>
        {this.renderTab(
          urlFor.userBooksRecent(login, app.storedQuery),
          reading,
          tabKind,
          BookmarkIcon,
          'profile.started',
        )}
        {this.renderTab(
          urlFor.userBooksReadLater(login, app.storedQuery),
          read_later,
          tabKind,
          PlusIcon,
          'profile.added',
        )}
        {this.renderTab(
          urlFor.userBooksFinished(login, app.storedQuery),
          finished,
          tabKind,
          FlagIcon,
          'profile.finished',
        )}
        {myself &&
          this.renderTab(
            urlFor.userBooksUploads(login, app.storedQuery),
            uploaded,
            tabKind,
            UploadIcon,
            'profile.own_uploads',
          )}
        {this.renderTab(
          urlFor.userBooksAll(login, app.storedQuery),
          total,
          tabKind,
          BookIcon,
          'profile.all',
        )}
      </UserSectionNav>
    )
  }

  // api doesn't send us special counters for audiobooks, and, moreover, the fields
  // for counters do not correspond to audiobook statuses;  WTF?
  renderSectionNavigationForAudiobooks(tabKind) {
    const {
      app,
      user: {
        login,
        counters: { total, reading, finished, read_later },
      },
    } = this.props

    return (
      <UserSectionNav>
        {this.renderTab(
          urlFor.userAudioBooksRecent(login, app.storedQuery),
          reading,
          tabKind,
          BookmarkIcon,
          'profile.started',
        )}
        {this.renderTab(
          urlFor.userAudioBooksNotFinished(login, app.storedQuery),
          read_later,
          tabKind,
          PlusIcon,
          'profile.added',
        )}
        {this.renderTab(
          urlFor.userAudioBooksFinished(login, app.storedQuery),
          finished,
          tabKind,
          FlagIcon,
          'profile.finished',
        )}
        {this.renderTab(
          urlFor.userAudioBooksAll(login, app.storedQuery),
          total,
          tabKind,
          BookIcon,
          'profile.all',
        )}
      </UserSectionNav>
    )
  }

  renderSectionNavigationForComicbooks(tabKind) {
    const {
      app,
      user: {
        login,
        counters: { total, reading, finished, read_later },
      },
    } = this.props

    return (
      <UserSectionNav>
        {this.renderTab(
          urlFor.userComicbooks(login, 'recent', app.storedQuery),
          reading,
          tabKind,
          BookmarkIcon,
          'profile.started',
        )}
        {this.renderTab(
          urlFor.userComicbooks(login, 'not_finished', app.storedQuery),
          read_later,
          tabKind,
          PlusIcon,
          'profile.added',
        )}
        {this.renderTab(
          urlFor.userComicbooks(login, 'finished', app.storedQuery),
          finished,
          tabKind,
          FlagIcon,
          'profile.finished',
        )}
        {this.renderTab(
          urlFor.userComicbooks(login, 'all', app.storedQuery),
          total,
          tabKind,
          BookIcon,
          'profile.all',
        )}
      </UserSectionNav>
    )
  }

  renderSectionContents() {
    const {
      route: { content },
      currentUser: {
        data: { comics: areComicsAvailable },
      },
    } = this.props

    if (content === 'books') {
      return this.renderBooks()
    } else if (content === 'audiobooks') {
      return this.renderAudiobooks()
    } else if (content === 'comicbooks' && areComicsAvailable) {
      return this.renderComicbooks()
    }
  }

  renderWrappedContent(content, contentType) {
    const {
      user: { pages },
      params: { filter: _filter },
    } = this.props
    const page = pages[contentType][_filter] // contentType can be 'books' or 'audiobooks' or 'comicbooks'
    return (
      <div>
        <InfiniteScroll load={this.load} page={page}>
          <UserSectionContentsWrapper>
            <List>{content}</List>
          </UserSectionContentsWrapper>
          <Spacer size={40} />
        </InfiniteScroll>
        <Spacer size={120} />
      </div>
    )
  }

  renderBooks() {
    const {
      user: {
        myself,
        ui: { loaded },
      },
      currentUser: {
        ui: {
          privacy: { data },
        },
      },
      books,
      params: { filter: _filter },
      i18n,
      app,
    } = this.props

    const canRenderLockIcon =
      data.content_visibility === privacyApiConstants.booksShow

    const booksInfo = infoByFilter[_filter]

    if (isEmpty(loaded) || !loaded.books || !loaded.books[_filter]) {
      return this.renderLoader()
    }

    if (loaded.books[_filter] && books.length === 0) {
      return this.renderEmptyState({
        text: i18n._(`profile.empty_${_filter}`),
        icon: booksInfo.icon,
        buttonLink: ['recent', 'not_finished'].includes(_filter)
          ? urlFor.library(app.storedQuery)
          : '',
        buttonText: ['recent', 'not_finished'].includes(_filter)
          ? i18n.t`shelf.add_book`
          : '',
      })
    }

    const renderedBooks = books.map((book, index) => (
      <Book
        key={`${book.uuid}-${index}`}
        book={book}
        kind={this.booksKind}
        hoverable={false}
        progress={myself && bookHelper.getProgressInPercent(book.library_card)}
        finished={myself && bookHelper.isFinished(book.library_card)}
        inPrivate={
          canRenderLockIcon && myself && bookHelper.isPrivate(book.library_card)
        }
        libraryCard={book.library_card}
      />
    ))

    return this.renderWrappedContent(renderedBooks, 'books')
  }

  renderAudiobooks() {
    const {
      user: {
        myself,
        ui: { loaded },
      },
      currentUser: {
        ui: {
          privacy: { data },
        },
      },
      params: { filter: _filter },
      audiobooks,
      i18n,
      app,
    } = this.props

    const canRenderLockIcon =
      data.content_visibility === privacyApiConstants.booksShow

    const booksInfo = infoByFilter[_filter]

    if (isEmpty(loaded) || !loaded.audiobooks || !loaded.audiobooks[_filter]) {
      return this.renderLoader()
    }

    if (loaded.audiobooks[_filter] && audiobooks.length === 0) {
      return this.renderEmptyState({
        text: i18n._(`profile.empty_${_filter}`),
        icon: booksInfo.icon,
        buttonLink: ['recent', 'not_finished'].includes(_filter)
          ? urlFor.library(app.storedQuery)
          : '',
        buttonText: ['recent', 'not_finished'].includes(_filter)
          ? i18n.t`shelf.add_book`
          : '',
      })
    }

    const renderedAudiobooks = audiobooks.map((audiobook, index) => (
      <AudioBook
        key={`${audiobook.uuid}-${index}`}
        audiobook={audiobook}
        kind={this.booksKind}
        hoverable={false}
        progress={myself && audioBookHelper.getProgressInPercent(audiobook)}
        finished={myself && audioBookHelper.isFinished(audiobook)}
        inPrivate={
          canRenderLockIcon && myself && audioBookHelper.isPrivate(audiobook)
        }
      />
    ))

    return this.renderWrappedContent(renderedAudiobooks, 'audiobooks')
  }

  renderComicbooks() {
    const {
      user: {
        myself,
        ui: { loaded },
      },
      currentUser: {
        ui: {
          privacy: { data },
        },
      },
      comicbooks,
      params: { filter: _filter },
      i18n,
      app,
    } = this.props

    const canRenderLockIcon =
      data.content_visibility === privacyApiConstants.booksShow

    const booksInfo = infoByFilter[_filter]

    if (isEmpty(loaded) || !loaded.comicbooks || !loaded.comicbooks[_filter]) {
      return this.renderLoader()
    }

    if (loaded.comicbooks[_filter] && comicbooks.length === 0) {
      return this.renderEmptyState({
        text: i18n._(`profile.empty_${_filter}`),
        icon: booksInfo.icon,
        buttonLink: ['recent', 'not_finished'].includes(_filter)
          ? urlFor.library(app.storedQuery)
          : '',
        buttonText: ['recent', 'not_finished'].includes(_filter)
          ? i18n.t`shelf.add_book`
          : '',
      })
    }

    const renderedComicbooks = comicbooks.map((comicbook, index) => (
      <Comicbook
        key={`${comicbook.uuid}-${index}`}
        comicbook={comicbook}
        kind={this.booksKind}
        path={urlFor.comicbook(comicbook.uuid, app.storedQuery)}
        progress={myself && comicbookHelper.getProgressInPercent(comicbook)}
        finished={myself && comicbookHelper.isFinished(comicbook)}
        inPrivate={
          canRenderLockIcon && myself && comicbookHelper.isPrivate(comicbook)
        }
      />
    ))

    return this.renderWrappedContent(renderedComicbooks, 'comicbooks')
  }

  renderEmptyState({ text, icon, buttonLink, buttonText }) {
    const link =
      buttonLink && buttonText ? { path: buttonLink, text: buttonText } : {}
    return (
      <div>
        <EmptyState message={text} icon={icon} link={link} />
        <Spacer size={'bottom-page'} />
      </div>
    )
  }

  renderLoader() {
    return (
      <div style={{ overflow: 'hidden' }}>
        <Spacer size={40} />
        <Loader color="gray" kind="big" centered />
        <Spacer size={'bottom-page'} />
      </div>
    )
  }

  renderLayoutSwitcher() {
    const {
      user: {
        login,
        ui: { layout },
      },
      route: { content },
    } = this.props

    return (
      <UserLayoutSwitcherBox content={content} login={login} layout={layout} />
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params, router }) {
    const { filter: _filter, username } = params
    const { dispatch, getState } = store
    const {
      currentUser: {
        data: { login, comics: areComicsAvailable },
      },
    } = getState()
    const contentType = [...router.routes].pop().content

    const load = []

    if (_filter === USER_BOOKS_UPLOADS) {
      if (env.isServer() && username !== login) {
        throw { response: { status: 403 } }
      }
    }
    if (
      env.isServer() &&
      ![
        USER_BOOKS_ALL,
        USER_BOOKS_UPLOADS,
        USER_BOOKS_RECENT,
        USER_BOOKS_FINISHED,
        USER_BOOKS_NOT_FINISHED,
      ].includes(_filter)
    ) {
      throw { response: { status: 404 } }
    }

    if (contentType === 'books') {
      load.push(dispatch(loadBooks(username, _filter)))
    } else if (contentType === 'audiobooks') {
      load.push(dispatch(loadAudioBooks(username, _filter)))
    } else if (contentType === 'comicbooks' && areComicsAvailable) {
      load.push(dispatch(loadComicbooks(username, _filter)))
    }

    await Promise.all(load)
  },
  {
    updateParams: shouldUpdateParams,
    userListener: true,
  },
)

const connectWrapper = connect((state, ownProps) => {
  const {
    params: { filter: _filter },
  } = ownProps
  return {
    app: state.app,
    currentUser: state.currentUser,
    user: buildUser(state, state.user.login),
    books: getBooksByIds(state, state.user.books[_filter]),
    audiobooks: getAudioBooksByIds(state, state.user.audiobooks[_filter]),
    comicbooks: getComicbooksByIds(state, state.user.comicbooks[_filter]),
  }
})

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(UserBooksBox)
